import { useMemo, useState } from 'react';
import { StediPayerSearch, type StediPayerSearchOption } from '@features/payer-search';
import { useAdminPayers, usePayers } from '@features/payer-search/store/hooks';
import { useDebounce } from '@hooks/useDebounce';
import { Box, MenuItem, Select, Typography } from '@mui/material';

import { initialPayerPlanTypes } from './payerPlans';
import { payerRosterRowId } from './payerRosterIdentity';
import { overlineSx, wf } from './tokens';
import type { PayerEnrollmentStatus, PayerRow } from './types';

/**
 * Which directory endpoint backs the search. Both scopes read the same STEDI
 * payer network and return the same row shape (`PayerResponse`); they differ
 * only in the endpoint's authorization: `admin` reads `GET /v2/admin/payers`,
 * `provider` reads `GET /v2/provider/payers`.
 */
export type IntakePayerSearchScope = 'admin' | 'provider';

interface IntakePayerSearchProps {
  existingRows: PayerRow[];
  onAdd: (row: PayerRow) => void;
  /**
   * Required, with no default: an unstated scope would quietly read the staff
   * endpoint and 403 for a provider, which is a runtime failure rather than a
   * build one.
   */
  scope: IntakePayerSearchScope;
}

const helperTextByScope: Record<IntakePayerSearchScope, string> = {
  admin: 'Admin only · select a payer to add it to the selected section',
  provider: 'Pick a payer to add it to your list.',
};

/** Uppercase field label above each control, per the "Payer List" design. */
const fieldLabelSx = {
  ...overlineSx,
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  lineHeight: 1.4,
  mb: '6px',
} as const;

/**
 * The search keeps its own "Payer" label for the input's accessible name; the
 * design shows that label as uppercase text above the field instead of floating.
 */
const hiddenFloatingLabelSx = {
  '& .MuiInputLabel-root': {
    clip: 'rect(0 0 0 0)',
    height: '1px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
  // MUI hides the placeholder while a floating label is unshrunk; ours is never visible.
  '& label[data-shrink=false] + .MuiInputBase-formControl .MuiInputBase-input::placeholder': {
    opacity: '0.42 !important',
  },
} as const;

/**
 * Read the payer directory through the endpoint the actor is allowed to use.
 *
 * Both hooks are called unconditionally (hook rules) and the unused one is
 * skipped, so exactly one request is ever in flight.
 */
function usePayerDirectory(scope: IntakePayerSearchScope, search: string | null) {
  const options = { perPage: 50, search };
  const adminResult = useAdminPayers(options, { skip: search === null || scope !== 'admin' });
  const providerResult = usePayers(options, { skip: search === null || scope !== 'provider' });
  return scope === 'admin' ? adminResult : providerResult;
}

export function IntakePayerSearch({ existingRows, onAdd, scope }: IntakePayerSearchProps) {
  const [search, setSearch] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<PayerEnrollmentStatus>('already_enrolled');
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { payers, isLoading, isFetching, isError } = usePayerDirectory(
    scope,
    debouncedSearch.length >= 2 ? debouncedSearch : null,
  );
  const options = useMemo<StediPayerSearchOption[]>(
    () =>
      payers.map(payer => ({
        avatarUrl: payer.avatar_url,
        displayName: payer.display_name,
        payerId: payer.primary_payer_id,
        stediId: payer.stedi_id,
      })),
    [payers],
  );
  const payerByStediId = useMemo(
    () => new Map(payers.map(payer => [payer.stedi_id, payer])),
    [payers],
  );
  const selectPayer = (option: StediPayerSearchOption | null) => {
    const payer = option ? (payerByStediId.get(option.stediId) ?? null) : null;
    if (!payer) return;
    const payerId = payer.primary_payer_id?.trim();
    if (!payerId) {
      setSelectionError('This payer does not have a primary payer ID and cannot be added.');
      return;
    }
    if (
      existingRows.some(row =>
        row.stediId ? row.stediId === payer.stedi_id : row.payerId === payerId,
      )
    ) {
      setSelectionError('That payer is already in the practice roster.');
      return;
    }
    onAdd({
      displayName: payer.display_name,
      enrollmentStatus,
      jotbill: false,
      // jotcred is derived from the section: payers who want to get
      // credentialed always include JotCred; credentialed-elsewhere payers never do.
      jotcred: enrollmentStatus === 'wants_to_enroll',
      payerId,
      rowId: payerRosterRowId({ payer_id: payerId, stedi_id: payer.stedi_id }),
      stediId: payer.stedi_id,
      programs: payer.programs ?? [],
      ptanRequired: payer.ptan_required ?? null,
      planTypes: initialPayerPlanTypes(payer.programs),
    });
    setSearch('');
    setSelectionError(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: '0 1 300px', minWidth: 220 }}>
          <Box component="label" id="payer-enrollment-section-label" sx={fieldLabelSx}>
            Add payer to
          </Box>
          <Select
            fullWidth
            labelId="payer-enrollment-section-label"
            onChange={event => setEnrollmentStatus(event.target.value)}
            size="small"
            sx={{ fontSize: '14px' }}
            value={enrollmentStatus}
          >
            <MenuItem value="already_enrolled">Already in-network</MenuItem>
            <MenuItem value="wants_to_enroll">Not in-network yet</MenuItem>
          </Select>
        </Box>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Box aria-hidden="true" component="span" sx={fieldLabelSx}>
            Payer
          </Box>
          <Box sx={hiddenFloatingLabelSx}>
            <StediPayerSearch
              error={isError}
              inputValue={search}
              label="Payer"
              loading={isLoading || isFetching}
              onInputChange={setSearch}
              onSelect={selectPayer}
              options={options}
              placeholder="Search payers"
            />
          </Box>
          {/* The search shows its own error and minimum-length hints in this slot. */}
          {!isError && search.length !== 1 && (
            <Typography sx={{ color: wf.meta, fontSize: '12px', mt: '4px' }}>
              {helperTextByScope[scope]}
            </Typography>
          )}
        </Box>
      </Box>
      {selectionError && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
          {selectionError}
        </Box>
      )}
    </>
  );
}
